/**
 * Tracks the child's weakness profile per error tag.
 *
 * Weakness score per tag is a decaying weighted measure in [0, 1].
 * Higher score = more problematic for the child.
 *
 * Update rule on each answered item:
 *   For each tag in item.error_tags:
 *     score' = score * decay + impact
 *     where impact = (isCorrect ? -0.15 : 0.30)
 *   Then clamp to [0, 1].
 *
 * Tags not present in the answered item are left untouched (so they
 * naturally fade in priority over time only if they reappear).
 */

import { WEAKNESS_DECAY, WEAKNESS_MAX_SCORE, WEAKNESS_MIN_SCORE } from '../data/constants.js';

export const KNOWN_ERROR_TAGS = [
  'tonismos',
  'ei_i_oi',
  'dipla_sygfona',
  'afonos_h',
  'omikron_omega',
  'epsilon_aita',
  'upsilon_oi',
  'ou_oi_diphthong',
  'gender_agreement',
  'verb_ending',
  'compound_words',
  // Internal tag carried only by the generic `g{N}_rule_spelling_attention`
  // rules — never appears on vocabulary items, so it cannot leak into the
  // weakness profile or into `findRuleForTags` results. Listed here to
  // satisfy the grammar-rule validator's KNOWN_ERROR_TAGS gate.
  'spelling_attention',
  // Internal tag carried only by the `g3_rule_final_n` lesson — standalone
  // rule for the «τελικό -ν» convention, never produced by vocabulary items.
  'final_n',
];

const WRONG_IMPACT = 0.3;
const CORRECT_RELIEF = 0.15;

export function clamp(value, min = WEAKNESS_MIN_SCORE, max = WEAKNESS_MAX_SCORE) {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

export function isKnownErrorTag(tag) {
  return KNOWN_ERROR_TAGS.includes(tag);
}

/**
 * Update the weakness profile after a single answered item.
 *
 * @param {object} profile - { tag: score } map; missing tags treated as 0
 * @param {string[]} itemTags - error_tags of the item that was answered
 * @param {boolean} isCorrect
 * @returns {object} new profile (never mutates input)
 */
export function updateWeaknessProfile(profile, itemTags, isCorrect) {
  const next = { ...profile };
  const impact = isCorrect ? -CORRECT_RELIEF : WRONG_IMPACT;

  for (const rawTag of itemTags || []) {
    if (!isKnownErrorTag(rawTag)) continue;
    const hadEntry = typeof next[rawTag] === 'number';
    // A correct answer should only relieve EXISTING weakness — never seed a
    // brand-new tag at score 0. Otherwise downstream metrics that treat low
    // scores as "improved" (e.g. weakness_improved badges) would unlock from
    // a single correct answer on a tag the child never actually struggled
    // with.
    if (!hadEntry && isCorrect) continue;
    const current = hadEntry ? next[rawTag] : 0;
    const updated = current * WEAKNESS_DECAY + impact;
    next[rawTag] = Number(clamp(updated).toFixed(3));
  }

  return next;
}

/**
 * Return the top N weakness tags sorted by score descending.
 * Tags with score 0 are excluded.
 */
export function getTopWeaknesses(profile, n = 2) {
  return Object.entries(profile || {})
    .filter(([tag, score]) => isKnownErrorTag(tag) && score > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([tag]) => tag);
}

/**
 * Filter items whose error_tags intersect with any of the given target tags.
 * Used by adaptive session composition.
 */
export function itemsMatchingTags(items, targetTags) {
  if (!targetTags || targetTags.length === 0) return [];
  const targetSet = new Set(targetTags);
  return items.filter((item) =>
    (item.error_tags || []).some((tag) => targetSet.has(tag))
  );
}

/**
 * Convert profile into a sorted array suitable for UI display.
 */
export function profileToSortedList(profile) {
  return Object.entries(profile || {})
    .filter(([tag]) => isKnownErrorTag(tag))
    .map(([tag, score]) => ({ tag, score }))
    .sort((a, b) => b.score - a.score);
}
